import { api } from "@/lib/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Check, Crown, Gift, Info, Sparkles, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { track, PRICING_EVENTS, CURRENCY_EVENTS, EVENTS } from "@/lib/analytics";
import { errorMessage, supportReference } from "@/lib/errors";

const TIER_RANK = { free: 0, pro: 1, premium: 2 };
const VALID_PERIODS = new Set(["monthly", "yearly"]);
const VALID_PLANS = new Set(["pro", "premium"]);
const CURRENCY_RE = /^[A-Z]{3}$/;

// Stripe checkout / billing portal domains our backend can legitimately return.
// We hard-allowlist the exact hostnames (never trust a wildcard/regex prefix)
// so that an attacker who can influence the response body can NOT redirect the
// customer to `checkout.stripe.com.attacker.net` or any lookalike domain.
const STRIPE_ALLOWED_HOSTS = new Set([
  "checkout.stripe.com",
  "billing.stripe.com",
]);

/**
 * Validates that `raw` is a real Stripe Checkout / Billing Portal URL.
 * Returns the parsed URL on success, or `null` on any failure (unparseable,
 * non-https, disallowed host). Never throws.
 */
function assertStripeUrl(raw) {
  if (typeof raw !== "string" || !raw) return null;
  let u;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  if (u.protocol !== "https:") return null;
  if (!STRIPE_ALLOWED_HOSTS.has(u.hostname)) return null;
  return u;
}


/**
 * Pricing page — 3-tier grid (Free / Pro / Premium) with a monthly-yearly
 * billing toggle. All amounts + copy come from `/api/pricing/plans` — nothing
 * is hardcoded here. Yearly savings are computed from the returned prices.
 *
 * URL query params (validated, invalid values ignored):
 *   ?plan=pro|premium    → highlights the target card
 *   ?period=monthly|yearly → preselects the toggle
 *   ?currency=CAD|USD|…  → overrides the display currency for this visit
 *                          (matches the currency chosen on the landing page)
 * Passed here from the landing page pricing CTA. We NEVER auto-open Stripe
 * Checkout — the user must click the plan CTA one final time to confirm.
 */
export default function Pricing() {
  const { user } = useAuth();
  const { currency: profileCurrency, format: profileFormat, supported: supportedList } = useCurrency();
  const location = useLocation();

  // Parse + validate query params (invalid → fallback silently).
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryPeriod = params.get("period");
  const queryPlan = params.get("plan");
  const queryCurrencyRaw = params.get("currency");
  // `queryCurrency` is the *shape*-valid ISO-3 code from the URL. It may still
  // be an unsupported code (e.g. 'ZZZ') — the effect below verifies it against
  // the backend supported list before we accept it as the display currency.
  const queryCurrency = queryCurrencyRaw && CURRENCY_RE.test(queryCurrencyRaw.toUpperCase())
    ? queryCurrencyRaw.toUpperCase()
    : null;
  const initialPeriod = VALID_PERIODS.has(queryPeriod) ? queryPeriod : "yearly";
  const highlightedPlan = VALID_PLANS.has(queryPlan) ? queryPlan : null;

  const [period, setPeriod] = useState(initialPeriod);   // monthly | yearly
  // Effective display currency: URL query override (validated against supported)
  // OR the user's saved profile currency. Never persists to profile.
  const [effectiveCurrency, setEffectiveCurrency] = useState(profileCurrency);
  const [data, setData] = useState(null);
  const [credits, setCredits] = useState(0);
  const [busy, setBusy] = useState(null);
  const targetCardRef = useRef(null);
  const scrolledRef = useRef(false);

  // Set of supported currency codes for O(1) validation.
  const supportedSet = useMemo(() => {
    const s = new Set(["USD"]); // always allowed
    (supportedList || []).forEach((c) => c?.code && s.add(String(c.code).toUpperCase()));
    return s;
  }, [supportedList]);

  // Re-sync from URL when nav changes (back/forward navigation).
  useEffect(() => {
    if (VALID_PERIODS.has(queryPeriod)) setPeriod(queryPeriod);
  }, [queryPeriod]);
  // Apply URL currency ONLY if it's in the supported list. Otherwise silently
  // fall through to the profile currency. Runs again when the supported list
  // finishes loading (avoids a race on first render).
  useEffect(() => {
    if (queryCurrency && supportedSet.has(queryCurrency)) {
      setEffectiveCurrency(queryCurrency);
    } else if (profileCurrency) {
      setEffectiveCurrency(profileCurrency);
    }
  }, [queryCurrency, profileCurrency, supportedSet]);

  useEffect(() => {
    api.get("/referrals").then((r) => setCredits(r.data.credits_months || 0)).catch(() => {});
    track(EVENTS.PRICING_VIEWED, { source: "app_pricing_page" });
  }, []);

  useEffect(() => {
    if (!effectiveCurrency) return; // wait for currency to resolve
    setData(null);
    api.get(`/pricing/plans?currency=${effectiveCurrency}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load pricing"));
  }, [effectiveCurrency]);

  // Once prices load and there's a highlighted plan from the URL, scroll it
  // into view (one-shot per mount).
  useEffect(() => {
    if (!data || !highlightedPlan || scrolledRef.current) return;
    if (targetCardRef.current) {
      targetCardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      scrolledRef.current = true;
    }
  }, [data, highlightedPlan]);

  const plans = data?.plans || [];
  const currency = effectiveCurrency;
  // Prefer a locale-aware formatter when the URL-supplied currency differs
  // from the profile currency (which the useCurrency hook is bound to).
  const format = useMemo(() => {
    if (!currency || currency === profileCurrency) return profileFormat;
    return (amount, opts = {}) => {
      const dec = opts.decimals ?? (Number.isInteger(amount) ? 0 : 2);
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency", currency,
          minimumFractionDigits: dec, maximumFractionDigits: dec,
        }).format(amount || 0);
      } catch {
        return `${currency} ${(amount || 0).toFixed(dec)}`;
      }
    };
  }, [currency, profileCurrency, profileFormat]);

  const setPeriodTracked = (next) => {
    if (!VALID_PERIODS.has(next) || next === period) return;
    setPeriod(next);
    track(PRICING_EVENTS.TOGGLE_CHANGED, {
      billing_period: next,
      currency,
      source_page: "pricing",
    });
  };

  const checkout = async (tier) => {
    // Double-click / concurrent-request guard. `busy` is set to the tier the
    // user is currently checking out; further clicks (on same or other card)
    // are no-ops until this settles or errors.
    if (busy) return;

    const planRow = plans.find((p) => p.tier === tier);
    const priceAmount = planRow?.prices?.[period]?.amount ?? null;
    track(PRICING_EVENTS.CHECKOUT_STARTED, {
      plan: tier,
      billing_period: period,
      displayed_price: priceAmount,
      currency,
      source_page: "pricing",
    });
    setBusy(tier);
    try {
      const { data: c } = await api.post("/payments/checkout", {
        tier,
        period,
        currency,
        origin_url: window.location.origin,
      });

      // Belt-and-braces validation before we hand the user off. Backend MUST
      // return a real Stripe URL — anything else is a config bug and we do
      // NOT want to `window.location = undefined` (blank page) or navigate
      // to an attacker-controlled host.
      const parsed = assertStripeUrl(c?.checkout_url);
      if (!parsed) {
        console.error(
          "[checkout] Backend returned an invalid Stripe checkout URL",
          { checkout_url: c?.checkout_url, response: c }
        );
        toast.error(
          "We couldn't start Stripe Checkout — please try again in a moment."
        );
        setBusy(null);
        return;
      }

      if (c.applied_referral_months > 0) {
        toast.success(
          `${c.applied_referral_months} free month(s) auto-applied at checkout`
        );
      }

      // Full-page browser navigation to an external URL. React Router's
      // `navigate()` would only push a client-side route (breaking the flow
      // + potentially blanking the app). `window.location.assign` keeps the
      // history entry so the user can hit BACK from Stripe to return here.
      try {
        window.location.assign(parsed.toString());
      } catch (navErr) {
        // Fatally rare, but if the browser blocks the redirect we still keep
        // the user inside CareerOS with a readable message.
        console.error("[checkout] window.location.assign failed", navErr);
        toast.error(
          "Your browser blocked the redirect to Stripe. Please disable pop-up blockers and try again."
        );
        setBusy(null);
      }
      // On successful redirect the page is unloading; do NOT clear busy here
      // (leaving the "Opening checkout…" label prevents re-clicks in the
      // millisecond gap before the browser swaps the document).
    } catch (e) {
      // Normalize FastAPI error `detail` — could be a string OR a dict like
      // `{message, support_reference}` (see `_customer_facing_checkout_error`
      // in `backend/routers/billing.py`). Passing a raw object to sonner used
      // to crash React and black-screen the whole app.
      const msg = errorMessage(e, "Checkout failed. Please try again.");
      const ref = supportReference(e);
      console.error("[checkout] failed", {
        status: e?.response?.status,
        detail: e?.response?.data?.detail_raw || e?.response?.data?.detail,
        support_reference: ref || undefined,
      });
      toast.error(ref ? `${msg} (ref: ${ref})` : msg);
      setBusy(null);
    }
  };

  const isLegacy = !!user?.legacy_pricing_id;
  const activeTier = user?.subscription_tier || "free";

  return (
    <div data-testid="pricing-page" className="pb-20">
      <PageHeader
        eyebrow="Pricing"
        title="Career OS, on your terms."
        subtitle="Localized to your currency. Cancel anytime. AI-powered — never mocked."
      />

      {/* Banners */}
      {isLegacy && (
        <div className="mb-6 max-w-3xl inline-flex items-center gap-2 rounded-lg bg-[hsl(var(--info))/0.10] border border-[hsl(var(--info))/0.35] px-4 py-3 text-sm text-[hsl(var(--info))]" data-testid="legacy-banner">
          <Info size={14} />
          <div>
            You're on our <b>legacy Pro plan</b> at your original price. Your rate is locked as long as your subscription stays active — thanks for being an early supporter.
          </div>
        </div>
      )}
      {credits > 0 && activeTier === "free" && (
        <div className="mb-6 inline-flex items-center gap-2 rounded-lg bg-[hsl(var(--warning))/0.12] border border-[hsl(var(--warning))/0.3] px-4 py-2 text-sm text-[hsl(var(--warning))]" data-testid="credit-banner">
          <Gift size={14} /> You have <b className="mx-1">{credits} free month{credits > 1 ? "s" : ""}</b> banked — auto-applied at checkout.
        </div>
      )}
      {activeTier !== "free" && !isLegacy && (
        <div className="mb-6 inline-flex items-center gap-2 rounded-lg bg-[hsl(var(--success))/0.12] border border-[hsl(var(--success))/0.3] px-4 py-2 text-sm text-[hsl(var(--success))]" data-testid="active-tier-badge">
          <Crown size={14} /> You're on <b className="mx-1">{activeTier}</b> — thank you!
        </div>
      )}

      {/* Monthly / Yearly toggle */}
      <div className="mb-8 inline-flex items-center gap-1 rounded-full bg-muted p-1" role="tablist" data-testid="period-toggle">
        <button
          data-testid="period-monthly"
          role="tab"
          aria-selected={period === "monthly"}
          onClick={() => setPeriodTracked("monthly")}
          className={`px-4 py-1.5 text-sm rounded-full transition-colors ${period === "monthly" ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
        >
          Billed monthly
        </button>
        <button
          data-testid="period-yearly"
          role="tab"
          aria-selected={period === "yearly"}
          onClick={() => setPeriodTracked("yearly")}
          className={`px-4 py-1.5 text-sm rounded-full transition-colors inline-flex items-center gap-1.5 ${period === "yearly" ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
        >
          Billed annually
          <span className="text-[10px] font-mono uppercase tracking-widest bg-[hsl(var(--success))/0.15] text-[hsl(var(--success))] px-1.5 py-0.5 rounded">
            2 mo free
          </span>
        </button>
      </div>

      {/* 3-tier grid */}
      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <PlanCard
            key={p.tier}
            plan={p}
            period={period}
            currency={currency}
            format={format}
            activeTier={activeTier}
            isLegacy={isLegacy}
            busy={busy === p.tier}
            highlighted={highlightedPlan === p.tier}
            innerRef={highlightedPlan === p.tier ? targetCardRef : null}
            onCheckout={() => checkout(p.tier)}
          />
        ))}
      </div>

      {/* Currency-mismatch notice (only when Stripe will charge in a
          different currency than we display). This preserves the user
          expectation that "what you see is what you pay". */}
      {data?.stripe_currency_matches_display === false && data?.stripe_charge_currency && (
        <div
          className="mt-6 flex items-start gap-2 rounded-lg border border-[hsl(var(--warning))/0.4] bg-[hsl(var(--warning))/0.08] text-[hsl(var(--warning))] px-4 py-3 text-sm max-w-3xl"
          data-testid="pricing-stripe-charge-notice"
        >
          <Info size={14} className="shrink-0 mt-0.5" />
          <div>
            Prices are shown in <b>{currency}</b> for reference. Stripe will
            process your payment in <b>{data.stripe_charge_currency}</b> —
            the exact amount charged will be displayed on the Stripe checkout
            screen before you confirm.
          </div>
        </div>
      )}

      {/* Footer note */}
      <div className="mt-6 text-center text-xs font-mono uppercase tracking-widest text-muted-foreground" data-testid="pricing-footer-note">
        Prices in {currency} · Stripe charges you in {data?.stripe_charge_currency || currency} · Cancel anytime · Test card 4242 4242 4242 4242
      </div>
    </div>
  );
}

function PlanCard({ plan, period, currency, format, activeTier, isLegacy, busy, highlighted, innerRef, onCheckout }) {
  const price = plan?.prices?.[period];
  const otherPrice = plan?.prices?.[period === "yearly" ? "monthly" : "yearly"];
  const amount = price?.amount ?? 0;
  const isFree = plan.tier === "free";

  // Effective monthly cost for yearly plans
  const effectiveMonthly = period === "yearly" && amount > 0 ? amount / 12 : null;

  // Savings vs. paying monthly for 12 months
  const yearlySavings = useMemo(() => {
    if (period !== "yearly") return null;
    if (!plan?.prices?.monthly?.amount || !plan?.prices?.yearly?.amount) return null;
    const raw = plan.prices.monthly.amount * 12 - plan.prices.yearly.amount;
    return raw > 0 ? raw : null;
  }, [plan, period]);

  const isCurrent = activeTier === plan.tier;
  // A "downgrade" here means: current tier already includes this plan. E.g.
  // a Premium user viewing the Pro card. We do NOT let them initiate a
  // sideways checkout — that would create a second subscription and
  // silently double-bill. Route them to the Billing Portal in Settings
  // where they can properly change or cancel their plan.
  const currentRank = TIER_RANK[String(activeTier || "free").toLowerCase()] ?? 0;
  const planRank = TIER_RANK[String(plan.tier).toLowerCase()] ?? 0;
  const isIncludedByCurrent = !isFree && !isCurrent && currentRank > planRank;
  const isDisabled = isFree || (isCurrent && !isLegacy) || isIncludedByCurrent;

  return (
    <div
      ref={innerRef}
      className={`bg-card border rounded-xl p-6 flex flex-col relative overflow-hidden transition-shadow ${
        highlighted
          ? "border-primary ring-2 ring-primary shadow-[0_0_40px_-8px_hsl(var(--primary)/0.35)]"
          : plan.highlight
            ? "border-border ring-2 ring-primary"
            : "border-border"
      }`}
      data-testid={`plan-${plan.tier}`}
    >
      {plan.highlight && (
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-mono uppercase tracking-widest" data-testid={`plan-${plan.tier}-badge`}>
          Popular
        </div>
      )}
      {plan.tier === "premium" && (
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] font-mono uppercase tracking-widest" data-testid={`plan-${plan.tier}-badge`}>
          Executive
        </div>
      )}

      <div className="flex items-center gap-2 mb-1">
        {plan.tier === "premium" && <Sparkles size={16} className="text-purple-500" />}
        {plan.tier === "pro" && <Zap size={16} className="text-primary" />}
        <div className="font-display font-bold text-xl tracking-tight" data-testid={`plan-${plan.tier}-name`}>{plan.name}</div>
      </div>
      <div className="text-sm text-muted-foreground mb-5 min-h-[36px]">{plan.tagline}</div>

      {/* Price block */}
      <div className="mb-5" data-testid={`plan-${plan.tier}-price-block`}>
        {isFree ? (
          <>
            <span className="font-display font-black text-4xl tracking-tight">Free</span>
            <span className="text-sm text-muted-foreground ml-2">forever</span>
          </>
        ) : (
          <>
            <div className="flex items-baseline gap-2">
              <span className="font-display font-black text-4xl tracking-tight" data-testid={`plan-${plan.tier}-amount`}>
                {price ? format(amount) : "—"}
              </span>
              <span className="text-sm text-muted-foreground">
                {period === "monthly" ? "per month" : "per year"}
              </span>
            </div>
            {period === "yearly" && effectiveMonthly !== null && (
              <div className="text-xs text-muted-foreground mt-1" data-testid={`plan-${plan.tier}-effective`}>
                ≈ {format(effectiveMonthly)}/mo · billed annually
              </div>
            )}
            {period === "monthly" && otherPrice?.amount > 0 && (
              <div className="text-xs text-[hsl(var(--success))] mt-1" data-testid={`plan-${plan.tier}-yearly-hint`}>
                Save {format(plan.prices.monthly.amount * 12 - otherPrice.amount, { decimals: 0 })}/yr when billed annually
              </div>
            )}
            {yearlySavings && (
              <div className="text-xs text-[hsl(var(--success))] mt-1" data-testid={`plan-${plan.tier}-savings`}>
                You save {format(yearlySavings, { decimals: 0 })} vs. monthly
              </div>
            )}
          </>
        )}
      </div>

      {/* Feature list */}
      <ul className="space-y-2 mb-6 flex-1" data-testid={`plan-${plan.tier}-features`}>
        {(plan.features || []).slice(0, 12).map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check size={14} className="text-[hsl(var(--success))] shrink-0 mt-1" />
            <span className="text-foreground/80">{f}</span>
          </li>
        ))}
        {(plan.features || []).length > 12 && (
          <li className="text-xs text-muted-foreground ml-6">+ {plan.features.length - 12} more…</li>
        )}
      </ul>

      <Button
        data-testid={`plan-cta-${plan.tier}`}
        variant={plan.highlight ? "default" : "outline"}
        disabled={isDisabled || busy}
        onClick={onCheckout}
        className="w-full"
      >
        {busy
          ? "Opening checkout…"
          : isFree
            ? "Current plan"
            : isCurrent && !isLegacy
              ? `You're on ${plan.name}`
              : isIncludedByCurrent
                ? `Included in your plan`
                : plan.cta}
      </Button>
      {isIncludedByCurrent && (
        <a
          href="/app/settings#billing"
          data-testid={`plan-manage-link-${plan.tier}`}
          className="mt-2 text-center text-xs text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
        >
          Manage plan in Settings →
        </a>
      )}
    </div>
  );
}
